import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Flame, Zap, BookOpen, Target, Clock, TrendingUp,
  Award, CheckCircle, AlertCircle, BarChart2,
} from 'lucide-react';

// ─── localStorage helpers ─────────────────────────────────────────────────────
const get = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key) ?? 'null') ?? fallback; } catch { return fallback; }
};
const set = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

const SESSIONS_KEY   = 'bramlo_study_sessions';
const MASTERY_KEY    = 'bramlo_flashcard_mastery_v1';
const QUIZ_KEY       = 'bramlo_quiz_results';
const STREAK_KEY     = 'bramlo_streak';

// ─── Streak logic ─────────────────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().slice(0, 10);
const yesterdayStr = () => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

function getStreak() {
  const data = get(STREAK_KEY, { streak: 0, lastDate: null });
  const today = todayStr();
  if (data.lastDate === today) return data;
  if (data.lastDate === yesterdayStr()) {
    const updated = { streak: data.streak + 1, lastDate: today };
    set(STREAK_KEY, updated);
    return updated;
  }
  const reset = { streak: 1, lastDate: today };
  set(STREAK_KEY, reset);
  return reset;
}

// ─── Week bar chart helper ────────────────────────────────────────────────────
function getWeekSessions() {
  const sessions = get(SESSIONS_KEY, []);
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const label = i === 0 ? 'Today' : ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
    const count = sessions.filter(s => s.date?.slice(0, 10) === key).length;
    days.push({ key, label, count });
  }
  return days;
}

// ─── Mini bar chart ───────────────────────────────────────────────────────────
const WeekChart = ({ days }) => {
  const maxCount = Math.max(...days.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '60px' }}>
      {days.map(d => (
        <div key={d.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <div style={{
            width: '100%',
            height: `${Math.max(4, (d.count / maxCount) * 52)}px`,
            borderRadius: '4px 4px 2px 2px',
            background: d.count > 0
              ? 'linear-gradient(180deg, #9333ea, #6366f1)'
              : 'var(--border)',
            transition: 'height 0.4s ease',
            minHeight: '4px',
          }} />
          <span style={{ fontSize: '9px', color: 'var(--foreground-secondary)', textAlign: 'center', opacity: 0.85 }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, accent = '#9333ea', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
    style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '14px',
      padding: '16px 18px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      minWidth: 0,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '9px',
        background: accent + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={16} color={accent} strokeWidth={1.8} />
      </div>
      <span style={{ fontSize: '12px', color: 'var(--foreground-secondary)', fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--foreground)', lineHeight: 1, letterSpacing: '-0.02em' }}>{value}</div>
    {sub && <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{sub}</div>}
  </motion.div>
);

// ─── Mastery ring ─────────────────────────────────────────────────────────────
const MasteryRing = ({ pct, label, color }) => {
  const r = 28, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: 'var(--foreground)' }}>
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke="var(--border)" strokeWidth="8" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ / 4}
          strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.7s ease' }} />
        <text x="36" y="40" textAnchor="middle" fontSize="13" fontWeight="800" fill="currentColor">{pct}%</text>
      </svg>
      <span style={{ fontSize: '11px', color: 'var(--foreground-secondary)', textAlign: 'center' }}>{label}</span>
    </div>
  );
};

// ─── Quiz history row ─────────────────────────────────────────────────────────
const QuizRow = ({ result }) => {
  const pct = result.total ? Math.round((result.score / result.total) * 100) : 0;
  const color = pct >= 80 ? '#34d399' : pct >= 60 ? '#fbbf24' : '#f87171';
  const d = new Date(result.date);
  const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '9px',
        background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <span style={{ fontSize: '13px', fontWeight: 800, color }}>{pct}%</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12.5px', color: 'var(--foreground)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {result.topic || 'Quiz'}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--foreground-secondary)' }}>
          {result.score}/{result.total} correct · {label}
        </div>
      </div>
      <div style={{ width: '60px', height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '2px', transition: 'width 0.5s' }} />
      </div>
    </div>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyHint = ({ icon: Icon, text }) => (
  <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--foreground-secondary)' }}>
    <Icon size={26} strokeWidth={1.3} style={{ marginBottom: '10px', opacity: 0.5 }} />
    <p style={{ fontSize: '12px', lineHeight: 1.5 }}>{text}</p>
  </div>
);

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const ProgressDashboard = () => {
  const [streak]      = useState(() => getStreak());
  const [weekDays]    = useState(() => getWeekSessions());
  const [quizResults] = useState(() => get(QUIZ_KEY, []).slice(-20).reverse());
  const [mastery]     = useState(() => get(MASTERY_KEY, {}));
  const [sessions]    = useState(() => get(SESSIONS_KEY, []));

  const stats = useMemo(() => {
    const today = todayStr();
    const sessionsToday = sessions.filter(s => s.date?.slice(0, 10) === today).length;
    const totalSessions = sessions.length;
    const masteryValues = Object.values(mastery);
    const knownCount = masteryValues.filter(v => v === 'know').length;
    const totalCards = masteryValues.length;
    const masteryPct = totalCards ? Math.round((knownCount / totalCards) * 100) : 0;
    const quizAvg = quizResults.length
      ? Math.round(quizResults.reduce((acc, r) => acc + (r.total ? r.score / r.total : 0), 0) / quizResults.length * 100)
      : null;
    const studyPacksGenerated = sessions.filter(s => s.type === 'study_pack').length;
    return { sessionsToday, totalSessions, masteryPct, knownCount, totalCards, quizAvg, studyPacksGenerated };
  }, [sessions, mastery, quizResults]);

  const recentSessions = sessions.slice(-5).reverse();

  return (
    <div style={{
      height: '100%',
      overflowY: 'auto',
      background: 'var(--background)',
      color: 'var(--foreground)',
      fontFamily: 'inherit',
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '28px 24px 48px' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          style={{ marginBottom: '28px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: 800, margin: '0 0 4px', letterSpacing: '-0.02em' }}>Your Progress</h1>
          <p style={{ fontSize: '13px', color: 'var(--foreground-secondary)', margin: 0 }}>
            Track your learning streak, quiz scores, and flashcard mastery.
          </p>
        </motion.div>

        {/* Top stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <StatCard icon={Flame}      label="Study Streak"      value={`${streak.streak}d`}  sub="consecutive days" accent="#f97316" delay={0.05} />
          <StatCard icon={Zap}        label="Sessions Today"    value={stats.sessionsToday}   sub={`${stats.totalSessions} total`} accent="#9333ea" delay={0.1} />
          <StatCard icon={Target}     label="Flashcard Mastery" value={`${stats.masteryPct}%`} sub={`${stats.knownCount}/${stats.totalCards} known`} accent="#34d399" delay={0.15} />
          <StatCard icon={BarChart2}  label="Quiz Average"      value={stats.quizAvg !== null ? `${stats.quizAvg}%` : '—'} sub={`${quizResults.length} quizzes taken`} accent="#60a5fa" delay={0.2} />
          <StatCard icon={BookOpen}   label="Study Packs"       value={stats.studyPacksGenerated} sub="generated from notes" accent="#f472b6" delay={0.25} />
        </div>

        {/* Week activity + mastery rings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>

          {/* Activity chart */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--foreground-secondary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TrendingUp size={13} /> This Week
            </div>
            {stats.totalSessions === 0
              ? <EmptyHint icon={BarChart2} text="Start studying to see your weekly activity chart" />
              : <WeekChart days={weekDays} />
            }
          </motion.div>

          {/* Mastery rings */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--foreground-secondary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Award size={13} /> Mastery Breakdown
            </div>
            {stats.totalCards === 0
              ? <EmptyHint icon={CheckCircle} text="Do flashcard sessions to track mastery by topic" />
              : (
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', paddingTop: '4px' }}>
                  <MasteryRing pct={stats.masteryPct} label="Overall" color="#9333ea" />
                  <MasteryRing
                    pct={stats.totalCards ? Math.round((Object.values(mastery).filter(v => v === 'learning').length / stats.totalCards) * 100) : 0}
                    label="Review" color="#f97316"
                  />
                  <MasteryRing
                    pct={stats.totalCards ? Math.round(((stats.totalCards - stats.knownCount - Object.values(mastery).filter(v => v === 'learning').length) / stats.totalCards) * 100) : 0}
                    label="Unseen" color="#374151"
                  />
                </div>
              )
            }
          </motion.div>
        </div>

        {/* Quiz history */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px', marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--foreground-secondary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Target size={13} /> Recent Quizzes
          </div>
          {quizResults.length === 0
            ? <EmptyHint icon={AlertCircle} text="Take quizzes in the Lumi AI chat or Notebook to see your history here" />
            : quizResults.slice(0, 8).map((r, i) => <QuizRow key={i} result={r} />)
          }
        </motion.div>

        {/* Recent activity feed */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--foreground-secondary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={13} /> Recent Activity
          </div>
          {recentSessions.length === 0
            ? <EmptyHint icon={BookOpen} text="Your study sessions will appear here. Open the Notebook and generate your first study pack!" />
            : recentSessions.map((s, i) => {
                const typeLabel = {
                  study_pack: '⚡ Study Pack',
                  quiz:       '📝 Quiz',
                  flashcards: '🃏 Flashcards',
                  notes:      '📋 Notes',
                }[s.type] || '📚 Study Session';
                const d = new Date(s.date);
                const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0', borderBottom: i < recentSessions.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <span style={{ fontSize: '11px', color: 'var(--foreground)', flex: 1 }}>
                      <strong style={{ color: '#9333ea' }}>{typeLabel}</strong>
                      {s.topic ? ` · ${s.topic}` : ''}
                    </span>
                    <span style={{ fontSize: '10.5px', color: 'var(--foreground-secondary)', whiteSpace: 'nowrap' }}>{label}</span>
                  </div>
                );
              })
          }
        </motion.div>

        {/* Tips footer */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
          style={{ marginTop: '24px', padding: '16px 20px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.22)', borderRadius: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#7c3aed', marginBottom: '8px' }}>💡 How to boost your score</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            {[
              'Open the Notebook and click "Study This Page" on any notes page',
              'Upload a PDF in Lumi AI chat, then ask for a quiz or flashcards',
              'Do flashcard sessions and mark cards as "Got It" to track mastery',
              'Study daily to maintain your streak — even 10 minutes counts',
            ].map((tip, i) => (
              <div key={i} style={{ fontSize: '11.5px', color: 'var(--foreground-secondary)', display: 'flex', gap: '6px' }}>
                <span style={{ color: '#9333ea', flexShrink: 0 }}>→</span> {tip}
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default ProgressDashboard;

// ─── Quiz result recorder ─────────────────────────────────────────────────────
// Call this from EnhancedQuizDeck when a quiz finishes to record the result.
export const recordQuizResult = (score, total, topic = '') => {
  try {
    const results = get(QUIZ_KEY, []);
    results.push({ date: new Date().toISOString(), score, total, topic });
    if (results.length > 200) results.splice(0, results.length - 200);
    set(QUIZ_KEY, results);
  } catch {}
};

// ─── Study session recorder ───────────────────────────────────────────────────
export const recordStudySession = (type = 'session', topic = '', duration = 0) => {
  try {
    const sessions = get(SESSIONS_KEY, []);
    sessions.push({ date: new Date().toISOString(), type, topic, duration });
    if (sessions.length > 500) sessions.splice(0, sessions.length - 500);
    set(SESSIONS_KEY, sessions);
    // Refresh streak
    getStreak();
  } catch {}
};
